'use no memo';
import React, { useContext, memo, useCallback, useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';
import { css } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage';
import { useEditSourceActions } from '@confluence/edit-common/entry-points/useEditSource';
import {
	CREATE_PAGE_SHORTCUT,
	CREATE_NAV_BUTTON_CLICK,
} from '@confluence/edit-common/entry-points/event-names';
import { CREATE_CONTENT } from '@confluence/named-routes/entry-points/routes';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener';
import { useKeyboardShortcutsEnabled } from '@confluence/shortcuts/entry-points/useKeyboardShortcutsEnabled';
import { ShortcutVisualizer, isMac } from '@confluence/shortcuts/entry-points/ShortcutVisualizer';
import {
	BLOG_SHORTCUT,
	UNIVERSAL_BLOG_SHORTCUT,
	GLOBAL_CREATE_SHORTCUT,
	UNIVERSAL_GLOBAL_CREATE_SHORTCUT_MAC,
	UNIVERSAL_GLOBAL_CREATE_SHORTCUT_WIN,
	UNIVERSAL_CREATE_LIVE_DOC_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts';
import { ConfluencePageSubType } from '@confluence/create-blank-fabric-page/entry-points/CreateBlankPageMutation';
import { UniversalCreate } from '@confluence/create-blank-fabric-page/entry-points/UniversalCreate';
import { GlobalCreateContentPopoverSpotlightLoader } from '@confluence/create-blank-fabric-page/entry-points/CreateContentPopover';
import { buildCreationLink } from '@confluence/create-blank-fabric-page/entry-points/createContentRouteUtils';
import { isCreateBlankFabricPageEnabled } from '@confluence/create-blank-fabric-page/entry-points/helpers';
import { useSpaceEnabledContentTypes } from '@confluence/create-blank-fabric-page/entry-points/useSpaceEnabledContentTypes';
import { WhiteboardGAModalManager } from '@confluence/whiteboards-onboarding/entry-points/WhiteboardGAModalManager';
import { LoomMigrationModalManager } from '@confluence/loom-onboarding/entry-points/LoomMigrationModalManager';
import { useSkippableCoordination } from '@confluence/skippable-coordination-client';
import { START_TOUCH } from '@confluence/navdex/entry-points/NavdexPointType';
import { EDITOR_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { preloadEditorOnboardingState } from '@confluence/onboarding-helpers/entry-points/preloadEditorOnboardingState';
import { usePageSpaceKey, usePageContentId } from '@confluence/page-context';
import { CreateContentButtonPreloader } from '@confluence/edit-button/entry-points/CreateContentButtonPreloader';
import { useIsWhiteboardFeatureEnabled } from '@confluence/whiteboard-utils/entry-points/useIsWhiteboardFeatureEnabled';
import { useIsCreationDropdownEnabled } from '@confluence/content-types-utils/entry-points/useIsCreationDropdownEnabled';
import { CreateButtonNudgeWrappable } from '@confluence/onboarding-hover-nudge/entry-points/BorderNudge';
import { HOVER_TARGET } from '@confluence/onboarding-hover-nudge/entry-points/constants/HoverTarget';
import { useOnboardingNewHomeTakeoverEnabled } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingNewHomeTakeover';
import { useHardStorageEnforcement } from '@confluence/storage-enforcement/entry-points/HardEnforcement/useHardStorageEnforcement';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled';
import { useSSRPlaceholderReplaceIdProp, LoadablePaint } from '@confluence/loadable';
import { fg } from '@confluence/feature-gating';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore';
import { useIsLivePagesFeatureEnabled } from '@confluence/live-pages-utils/entry-points/useIsLivePagesFeatureEnabled';
import { withFlags } from '@confluence/flags/entry-points/withFlags';
import { ConfluenceSmartCreationModalLoader } from '@confluence/smart-create/entry-points/smartCreationModal';

import {
	EngagementInlineDialogLoader as EngagementInlineDialogLoaderNav4,
	EngagementInlineDialogLoaderNav3,
} from './EngagementInlineDialogLoader';
import { CreateButton as CreateButtonNav3 } from './Nav3/CreateButton';

const containerStyles = css({
	display: 'flex',
	alignItems: 'center',
});

const CreateButton = LoadablePaint<any>({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-TopNav" */ '@atlaskit/navigation-system/top-nav-items'
			)
		).CreateButton,
});

const LIVE_DOCS_LINK =
	'https://support.atlassian.com/confluence-cloud/docs/create-and-collaborate-in-real-time-with-live-docs/';

const CreateComponentContent = ({ flags }) => {
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	const isNav4Enabled = useIsNav4Enabled();
	const { setQueryParams, match, push, location } = useContext(RoutesContext);
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isWhiteboardGAOnboardingEnabled } = useIsWhiteboardFeatureEnabled();
	const isCreationDropdownEnabled = useIsCreationDropdownEnabled();
	const { enforceStorageLimit, shouldBlockCreate } = useHardStorageEnforcement({
		source: 'create-component',
	});
	const [areKeyboardShortcutsEnabled] = useKeyboardShortcutsEnabled();
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();

	const isCreateContentPage = Boolean(CREATE_CONTENT.match(location?.pathname || ''));

	const isOnEditRoute = useIsEditorPage();
	const { isLivePagesFeatureEnabled } = useIsLivePagesFeatureEnabled();
	const { isCreateLiveDocEnabled } = useSpaceEnabledContentTypes(spaceKey);

	// Whiteboard GA

	const [isWhiteboardGASpotlightEnabled, setIsWhiteboardGASpotlightEnabled] = useState(false);

	// GA onboarding is enabled + NOT on editor route + NOT on create-content route
	// Exclude the editor route to avoid disrupting a user's workflow when they're actively creating
	// Exclude the create-content route because it can quickly redirect and we don't want modal screen events firing twice
	const showWhiteboardGAOnboardingModal =
		isWhiteboardGAOnboardingEnabled() && !isOnEditRoute && !isCreateContentPage;

	const [hasStartedWhiteboardGAOnboardingCoordination, stopWhiteboardGAOnboardingCoordination] =
		useSkippableCoordination(
			'cc-whiteboards-ga-admin-onboarding-modal',
			!showWhiteboardGAOnboardingModal,
		);

	const onCloseWhiteboardGAOnboardingModal = useCallback(() => {
		void stopWhiteboardGAOnboardingCoordination();
		setIsWhiteboardGASpotlightEnabled(true);
	}, [setIsWhiteboardGASpotlightEnabled, stopWhiteboardGAOnboardingCoordination]);

	const onCloseWhiteboardGASpotlight = useCallback(() => {
		setIsWhiteboardGASpotlightEnabled(false);
	}, [setIsWhiteboardGASpotlightEnabled]);

	const [hasStartedLoomPremigrationModalCoordination, stopLoomPremigrationModalCoordination] =
		useSkippableCoordination(
			'cc-content-types-loom-premigration-modal',
			!fg('confluence_loom_premigration_modal'),
		);
	const onCloseLoomPremigrationModal = useCallback(() => {
		void stopLoomPremigrationModalCoordination();
	}, [stopLoomPremigrationModalCoordination]);

	const [hasStartedLoomMigrationModalCoordination, stopLoomMigrationModalCoordination] =
		useSkippableCoordination(
			'cc-content-types-loom-migration-modal',
			!fg('confluence_loom_migration_modal'),
		);
	const onCloseLoomMigrationModal = useCallback(() => {
		void stopLoomMigrationModalCoordination();
	}, [stopLoomMigrationModalCoordination]);

	const { isEnabled: isOnboardingNewHomeTakeoverEnabled } = useOnboardingNewHomeTakeoverEnabled();

	const { setEditSource } = useEditSourceActions();
	const { data } = useGetOnboardingState(
		Object.values({
			...EDITOR_ONBOARDING_STATE_KEYS,
		}),
		!fg('cc_onboarding_experience'),
	);

	const { editorOnboardingEligiblity } = deserializeState(data);

	const fireAnalytics = (additionalAttributes = {}) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'globalNavigation',
				action: 'clicked',
				actionSubject: 'navigationItem',
				actionSubjectId: 'create',
				attributes: {
					selectedItemPageContext: match ? match.name : undefined,
					navigationLayer: 'global',
					navVersion: isNav4Enabled ? '4' : '3',
					navdexPointType: START_TOUCH,
					...additionalAttributes,
				},
			},
		}).fire();
	};

	const openCreateDialog = () => {
		fireAnalytics();
		setQueryParams({ createDialog: true });
	};

	const shouldCreateBlankFabricPage = (): boolean => {
		if (data && editorOnboardingEligiblity) {
			void preloadEditorOnboardingState();
		}

		return true;
	};

	const createPageHref = buildCreationLink({
		contentType: 'page',
		source: 'createBlankFabricPage',
		withFallback: true,
		spaceKey,
		parentPageId: contentId,
	});

	const handleCreatePageShortcut = () => {
		setEditSource(CREATE_PAGE_SHORTCUT);
		fireAnalytics({ keyboardShortcut: true });
		const shouldCreate = shouldCreateBlankFabricPage();

		if (shouldCreate) {
			push(createPageHref);
		}
	};
	const handleCreatePageButtonClick = (e: React.MouseEvent<HTMLElement>) => {
		fireAnalytics();
		const shouldCreate = shouldCreateBlankFabricPage();

		if (shouldCreate) {
			if (isNav4Enabled) {
				if (e.metaKey || e.ctrlKey) {
					window.open(createPageHref, '_blank', 'noreferrer');
				} else {
					push(createPageHref);
				}
			}
		} else {
			// creation is handled by href for button click
			e.preventDefault();
		}
	};

	const createBlog = () => {
		const createBlogLink = buildCreationLink({
			contentType: 'blog',
			source: 'globalCreateBlogShortcut',
			spaceKey,
		});
		push(createBlogLink);
	};

	const createLiveDoc = () => {
		const createLiveDocLink = buildCreationLink({
			contentType: 'page',
			subType: ConfluencePageSubType.LIVE,
			parentPageId: contentId,
			withFallback: true,
			source: 'globalCreateLiveDocShortcut',
			spaceKey,
		});
		push(createLiveDocLink);
	};

	const showLiveDocNotEnabledFlag = () => {
		void flags.showErrorFlag({
			id: 'live-doc-not-enabled-shortcut-flag',
			title: <FormattedMessage {...i18n.liveDocsNotEnabledTitle} />,
			description: <FormattedMessage {...i18n.liveDocsNotEnabledDescription} />,
			isAutoDismiss: true,
			actions: [
				{
					content: <FormattedMessage {...i18n.liveDocsNotEnabledAction} />,
					onClick: () => {
						window.open(LIVE_DOCS_LINK, '_blank');
					},
				},
			],
		});
	};

	const getButtonHref = () => {
		if (isCreateBlankFabricPageEnabled() && !shouldBlockCreate) {
			return createPageHref;
		}
		return undefined;
	};

	const [createPageClickListener, createPageShortcutListener] = isCreateBlankFabricPageEnabled()
		? [handleCreatePageButtonClick, handleCreatePageShortcut]
		: [openCreateDialog, openCreateDialog];

	/**
   * Please also update skeleton in appNavigationLazyItems
   *
   * <NavItemWrapper>
      <CreateButton />
    </NavItemWrapper>
   */

	const EngagementInlineDialogLoader = isNav4Enabled
		? EngagementInlineDialogLoaderNav4
		: EngagementInlineDialogLoaderNav3;

	const globalCreateContentPopover = useCallback(() => {
		if (hasStartedWhiteboardGAOnboardingCoordination || isWhiteboardGASpotlightEnabled) {
			return (
				<CreateButtonNudgeWrappable
					target={HOVER_TARGET.CREATE}
					shouldWrap={isOnboardingNewHomeTakeoverEnabled}
				>
					<GlobalCreateContentPopoverSpotlightLoader
						isGA
						isUniversalCreate
						spotlightEnabled={isWhiteboardGASpotlightEnabled}
						onSpotlightClose={onCloseWhiteboardGASpotlight}
					/>
				</CreateButtonNudgeWrappable>
			);
		}

		return (
			<CreateButtonNudgeWrappable
				target={HOVER_TARGET.CREATE}
				shouldWrap={isOnboardingNewHomeTakeoverEnabled}
			>
				<UniversalCreate />
			</CreateButtonNudgeWrappable>
		);
	}, [
		isWhiteboardGASpotlightEnabled,
		onCloseWhiteboardGASpotlight,
		isOnboardingNewHomeTakeoverEnabled,
		hasStartedWhiteboardGAOnboardingCoordination,
	]);

	const universalGlobalCreateShortcut = isMac()
		? UNIVERSAL_GLOBAL_CREATE_SHORTCUT_MAC
		: UNIVERSAL_GLOBAL_CREATE_SHORTCUT_WIN;

	const handlePreload = () => {
		if (fg('aifc_create_enabled')) {
			// preload smart creation
			void ConfluenceSmartCreationModalLoader.preload();
		}
	};

	return (
		// disabling this for now, will move preloading to trigger button when it supports onMouseEnter
		<div
			data-testid="app-navigation-create"
			data-pendo-id="con-*/wiki/home-create"
			data-vc="create-component"
			{...ssrPlaceholderIdProp}
			css={containerStyles}
			onMouseEnter={handlePreload}
			onFocus={handlePreload}
		>
			<EngagementInlineDialogLoader
				engagementId="createComponentButton"
				dataVC="app-navigation-create-engagement-inline-dialog"
			>
				{isCreationDropdownEnabled ? (
					globalCreateContentPopover()
				) : (
					<Tooltip
						content={
							areKeyboardShortcutsEnabled && (
								<ShortcutVisualizer shortcut={universalGlobalCreateShortcut} isTooltip />
							)
						}
					>
						<CreateContentButtonPreloader editSource={CREATE_NAV_BUTTON_CLICK}>
							<CreateButtonNudgeWrappable
								target={HOVER_TARGET.CREATE}
								shouldWrap={isOnboardingNewHomeTakeoverEnabled}
							>
								{isNav4Enabled ? (
									<CreateButton onClick={enforceStorageLimit(createPageClickListener)}>
										<FormattedMessage {...i18n.create} />
									</CreateButton>
								) : (
									<CreateButtonNav3
										onClick={enforceStorageLimit(createPageClickListener)}
										href={getButtonHref()}
									/>
								)}
							</CreateButtonNudgeWrappable>
						</CreateContentButtonPreloader>
					</Tooltip>
				)}
			</EngagementInlineDialogLoader>
			<GeneralShortcutListener
				deprecatedAccelerator={
					!(isLiveEditMode || isOnEditRoute) ? GLOBAL_CREATE_SHORTCUT : undefined
				}
				accelerator={universalGlobalCreateShortcut}
				listener={enforceStorageLimit(createPageShortcutListener)}
				isAppNavigationShortcut
			/>
			<GeneralShortcutListener
				deprecatedAccelerator={!(isLiveEditMode || isOnEditRoute) ? BLOG_SHORTCUT : undefined}
				accelerator={UNIVERSAL_BLOG_SHORTCUT}
				listener={enforceStorageLimit(createBlog)}
				isAppNavigationShortcut
			/>
			{isCreateLiveDocEnabled && isLivePagesFeatureEnabled && (
				<GeneralShortcutListener
					accelerator={UNIVERSAL_CREATE_LIVE_DOC_SHORTCUT}
					listener={enforceStorageLimit(createLiveDoc)}
					isAppNavigationShortcut
				/>
			)}
			{!isCreateLiveDocEnabled && isLivePagesFeatureEnabled && (
				<GeneralShortcutListener
					accelerator={UNIVERSAL_CREATE_LIVE_DOC_SHORTCUT}
					listener={enforceStorageLimit(showLiveDocNotEnabledFlag)}
					isAppNavigationShortcut
				/>
			)}
			{showWhiteboardGAOnboardingModal && hasStartedWhiteboardGAOnboardingCoordination && (
				<WhiteboardGAModalManager
					onClose={onCloseWhiteboardGAOnboardingModal}
					parentId={contentId}
					spaceKey={spaceKey}
				/>
			)}
			{
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				hasStartedLoomPremigrationModalCoordination &&
					fg('confluence_loom_beta_enabled') &&
					fg('confluence_loom_premigration_modal') && (
						<LoomMigrationModalManager isPremigration onClose={onCloseLoomPremigrationModal} />
					)
			}
			{hasStartedLoomMigrationModalCoordination &&
				// Migration
				// This feature gate has the rule: `loombetaprovision` none of `migrated-site` => 100% fail
				// since there not a different feature gate to let us know if the user is migrated
				fg('confluence_loom_migration_modal') && (
					<LoomMigrationModalManager onClose={onCloseLoomMigrationModal} />
				)}
		</div>
	);
};

const i18n = defineMessages({
	// eslint-disable-next-line @atlassian/i18n/require-description
	create: {
		id: 'app-navigation.top-navigation.create',
		defaultMessage: 'Create',
	},
	liveDocsNotEnabledTitle: {
		id: 'app-navigation.shortcuts.flags.liveDocsNotEnabled.title',
		description:
			'Title for the flag that informs the user that live docs are not enabled in this space when using the create shortcut',
		defaultMessage: 'Live docs aren’t enabled in this space',
	},
	liveDocsNotEnabledDescription: {
		id: 'app-navigation.shortcuts.flags.liveDocsNotEnabled.description',
		description:
			'Description for the flag that informs the user that live docs are not enabled in this space when using the create shortcut',
		defaultMessage: 'To use this shortcut, your space admin will need to enable live docs.',
	},
	liveDocsNotEnabledAction: {
		id: 'app-navigation.shortcuts.flags.liveDocsNotEnabled.action',
		description:
			'Live Docs Documentation Link for the flag that informs the user that live docs are not enabled in this space when using the create shortcut',
		defaultMessage: 'Find out more',
	},
});

export const CreateComponent = withFlags(memo(CreateComponentContent));
